import { useConfigurationStore } from '../../stores/useConfigurationStore';
import { parseStatusBuilderWireSection } from './statusBuilderWire';
import { useParameterWorkflowStore } from '../../stores/useParameterWorkflowStore';
import { useQuickFillStore } from '../../stores/useQuickFillStore';
import { useSharedParameterStore } from '../../stores/useSharedParameterStore';
import { useUIStore } from '../../stores/useUIStore';
import {
  applyMappingScopeEditorProfileState,
} from './mappingScopeEditorProfileState';
import {
  deriveBoltConfigurationFromProfileMappings,
  getBoltProfileObjectTypeName,
  getBoltProfileObjectTypeAttributes,
  isBoltProfileMappingAttribute,
  isBoltProfileTargetAliasAttributeName,
  normalizeBoltProfileKey,
} from '../../lib/boltProfileMapping';
import {
  getPipeexUiProfileMetadata,
  restoreQuickFillProfileConfiguration,
  type PipeexUiWizardSnapshot,
} from './pipeexProfileUiState';
import {
  PIPEEX_UI_ADDITIONAL_CATEGORIES_FIELD_IDS,
  PIPEEX_UI_BOLT_FIELD_IDS,
  PIPEEX_UI_CONNECTOR_FIELD_IDS,
} from './pipeexWizardProfileState';
import type {
  BoltConfiguration,
  ObjectTypeMapping,
} from '../../types';
import { summarizeProfileBoltConfigurationForDebug } from './pcfSettingsDtoApplyDebug';
import {
  logPcfSettingsDebug,
  summarizePcfSettingsDtoDataFlowForDebug,
  summarizePcfSettingsDtoForDebug,
  snapshotCurrentPcfSettingsStoreForDebug,
} from './pcfSettingsDebug';
import { type PcfSettingsDto } from './pcfSettingsTypes';
import { buildLegacyPcfObjectTypeMap } from './legacyPcfMappingSections';
import { getProfileObjectTypeSeeds, restoreProfileAttributeEditability } from './mappingProfileSnapshot';
import { captureAppliedPcfCatalogSections } from '../../stores/pcfProfileCatalogState';
import {
  convertLegacyIsoArrayToMap,
  getTextValue,
  normalizeBoltCalculationType,
  normalizeBomParameterType,
  normalizeSystemGroupingPreference,
  parseJson,
} from './pcfSettingsSerialization';

function debugPcfSettings(message: string, payload?: unknown): void {
  logPcfSettingsDebug('pcfSettingsApply', message, payload);
}

interface ApplyPipeexSettingsOptions {
  resetProjectRuntimeState?: boolean;
  restoreWizardProgress?: boolean;
}

function applyProfileFormFieldSnapshot<TConfig extends Record<string, any>>(
  config: TConfig,
  fields: Partial<Record<string, string>> | undefined,
  fieldIds?: readonly string[],
  replaceMissingFields = false
): TConfig {
  if (!fields && !fieldIds) {
    return config;
  }

  const fieldsToApply = fieldIds ?? Object.keys(fields ?? {});

  return fieldsToApply.reduce<TConfig>((nextConfig, fieldId) => {
    const currentField = nextConfig[fieldId];
    if (
      currentField &&
      typeof currentField === 'object' &&
      'value' in currentField
    ) {
      const hasProfileValue =
        fields !== undefined &&
        Object.prototype.hasOwnProperty.call(fields, fieldId);
      if (!hasProfileValue && !replaceMissingFields) {
        return nextConfig;
      }

      return {
        ...nextConfig,
        [fieldId]: {
          ...currentField,
          value: hasProfileValue ? getTextValue(fields?.[fieldId]) : '',
          error: undefined,
        },
      };
    }

    return nextConfig;
  }, config);
}

function stripBoltProfileRowsByChoice(
  objectTypeMap: Map<string, ObjectTypeMapping>,
  bolt: BoltConfiguration
): void {
  const targetObjectTypeName = getBoltProfileObjectTypeName(
    bolt.boltModelingChoice.value === 'yes'
  );
  ['BOLT', 'GASKET'].forEach((objectTypeName) => {
    const objectType = getProfileObjectTypeByName(objectTypeMap, objectTypeName);
    if (!objectType) {
      return;
    }

    const isTarget = normalizeBoltProfileKey(objectTypeName) === targetObjectTypeName;
    const attributes = objectType.attributes.filter((attribute) => {
      if (!isTarget) {
        return !isBoltProfileMappingAttribute(attribute);
      }

      return !isBoltProfileTargetAliasAttributeName(attribute.pcfAttributeName);
    });

    objectTypeMap.set(objectType.name, {
      ...objectType,
      attributes: attributes.map((attribute, index) => ({
        ...attribute,
        order: index + 1,
      })),
    });
  });
}

function getProfileObjectTypeByName(
  objectTypeMap: Map<string, ObjectTypeMapping>,
  objectTypeName: string
): ObjectTypeMapping | undefined {
  const normalizedObjectTypeName = normalizeBoltProfileKey(objectTypeName);
  return Array.from(objectTypeMap.values()).find(
    (candidate) => normalizeBoltProfileKey(candidate.name) === normalizedObjectTypeName
  );
}

function summarizeProfileBoltRowsForDebug(
  objectTypeMap: Map<string, ObjectTypeMapping>
): Array<{
  objectType: string;
  pcfAttributeName: string;
  revitParam: string;
  attributeType: string;
}> {
  const objectTypes = Array.from(objectTypeMap.values());

  return ['BOLT', 'GASKET'].flatMap((objectTypeName) =>
    getBoltProfileObjectTypeAttributes(objectTypes, objectTypeName)
      .filter((attribute) =>
        isBoltProfileMappingAttribute(attribute)
      )
      .map((attribute) => ({
        objectType: objectTypeName,
        pcfAttributeName: attribute.pcfAttributeName ?? '',
        revitParam: attribute.revitParam ?? '',
        attributeType: attribute.attributeType ?? '',
      }))
  );
}

function restoreParameterWorkflowProfileSnapshot(
  wizard: PipeexUiWizardSnapshot | undefined
): void {
  if (!wizard) {
    return;
  }

  useParameterWorkflowStore.setState((state) => ({
    ...state,
    workflowMode: wizard.setup.workflowMode,
    useDefaultFile: wizard.setup.useDefaultFile,
    sharedParameterFilePath: wizard.setup.sharedParameterFilePath,
    parameters: wizard.setup.parameters.map((parameter) => ({ ...parameter })),
    selectedCategories: [...wizard.setup.selectedCategories],
    familiesWithParameters: [],
    systemGroupingPreference: wizard.setup.systemGroupingPreference,
  }));
  useParameterWorkflowStore.getState().validateConfiguration();

  if (wizard.setup.useDefaultFile) {
    useSharedParameterStore.getState().loadDefaultParameters();
  } else if (
    typeof wizard.setup.sharedParameterFileContent === 'string' &&
    wizard.setup.sharedParameterFileContent.trim().length > 0
  ) {
    const sharedParameterState = useSharedParameterStore.getState();
    sharedParameterState.setUseDefaultFile(false);
    sharedParameterState.setFilePath(wizard.setup.sharedParameterFilePath);
    sharedParameterState.loadFileContent(wizard.setup.sharedParameterFileContent);
  }
}

export function applyPipeexSettingsToConfigurationStore(
  dto: PcfSettingsDto,
  options: ApplyPipeexSettingsOptions = {}
): void {
  captureAppliedPcfCatalogSections(dto);
  const store = useConfigurationStore.getState();
  const uiStore = useUIStore.getState();
  const quickFillStore = useQuickFillStore.getState();
  const beforeSnapshot = snapshotCurrentPcfSettingsStoreForDebug();
  debugPcfSettings('[pcfSettingsApply] applyPipeexSettingsToConfigurationStore start', {
    summary: summarizePcfSettingsDtoForDebug(dto),
    dataFlow: summarizePcfSettingsDtoDataFlowForDebug(dto),
    beforeProcess: beforeSnapshot.process,
  });

  const bom = parseJson<any>(dto.bomParameters, {});
  const isoRaw = parseJson<any>(dto.isoParameters, {});
  const isoMap = Array.isArray(isoRaw)
    ? convertLegacyIsoArrayToMap(isoRaw)
    : (isoRaw && typeof isoRaw === 'object' ? isoRaw as Record<string, any[]> : {});
  const validationMap = parseJson<Array<{ key?: string; value?: string; Key?: string; Value?: string }>>(
    dto.validationMap,
    []
  );
  const advancedRaw = parseJson<any>(dto.advancedSettings, {});
  const advancedBase = typeof advancedRaw?.base === 'string'
    ? parseJson<any>(advancedRaw.base, {})
    : advancedRaw;

  // Status Builder hydration: the profile's PascalCase wire section (if present)
  // replaces the store categories; an absent section keeps the current defaults.
  try {
    const statusBuilder = parseStatusBuilderWireSection(advancedBase);
    if (statusBuilder) {
      useConfigurationStore.getState().setStatusBuilderConfiguration(statusBuilder);
    }
  } catch (error) {
    console.error('[pcfSettingsApply] Failed to hydrate Status Builder section:', error);
  }

  const profileUiMetadata = getPipeexUiProfileMetadata(dto.advancedSettings);
  const wizard = profileUiMetadata?.wizard;
  const isFullWizardProfile = profileUiMetadata?.version === 2 && Boolean(wizard);
  if (options.resetProjectRuntimeState !== false) {
    uiStore.resetProjectScopedRuntimeState();
  }

  const bomArray = Array.isArray(bom) ? bom : [];
  const projectParameters = Array.isArray(bom?.ProjectParameters)
    ? bom.ProjectParameters
    : bomArray.filter((entry: any) => normalizeBomParameterType(entry?.ParameterType ?? entry?.parameterType) === 1);
  const systemParameters = Array.isArray(bom?.SystemParameters)
    ? bom.SystemParameters
    : bomArray.filter((entry: any) => normalizeBomParameterType(entry?.ParameterType ?? entry?.parameterType) === 2);
  const bomColumnParameters = Array.isArray(bom?.BomParameters)
    ? bom.BomParameters
    : bomArray.filter(
        (entry: any) =>
          normalizeBomParameterType(entry?.ParameterType ?? entry?.parameterType) === 3
      );

  const getNamedParameterValue = (entries: any[], name: string): string => {
    const normalizedName = name.trim().toUpperCase();
    const match = entries.find(
      (entry: any) => getTextValue(entry?.Name).trim().toUpperCase() === normalizedName
    );
    return getTextValue(match?.RevitParameter);
  };
  const getProfileProcessValue = (fieldId: string, fallback: string): string => {
    if (
      wizard?.processProperties.fields &&
      Object.prototype.hasOwnProperty.call(wizard.processProperties.fields, fieldId)
    ) {
      return getTextValue(
        wizard.processProperties.fields[
          fieldId as keyof typeof wizard.processProperties.fields
        ]
      );
    }

    if (
      profileUiMetadata?.processProperties &&
      Object.prototype.hasOwnProperty.call(profileUiMetadata.processProperties, fieldId)
    ) {
      return getTextValue(
        profileUiMetadata.processProperties[
          fieldId as keyof typeof profileUiMetadata.processProperties
        ]
      );
    }

    return isFullWizardProfile ? '' : fallback;
  };

  const mappedProjectProperties = projectParameters.map((p: any, idx: number) => ({
    id: `project-${idx + 1}`,
    name: getTextValue(p?.Name),
    value: getTextValue(p?.RevitParameter),
  }));

  const mappedSystemProperties = systemParameters.map((p: any, idx: number) => ({
    id: `system-${idx + 1}`,
    name: getTextValue(p?.Name),
    value: getTextValue(p?.RevitParameter),
  }));

  const existingObjectTypes = getProfileObjectTypeSeeds({
    isFullWizardProfile,
    persistedObjectTypeNames: wizard?.mapping.objectTypeNames,
    existingObjectTypes: store.mappingConfiguration.general.mappingTable.objectType || [],
  });
  const objectTypeMap = restoreProfileAttributeEditability(
    buildLegacyPcfObjectTypeMap({
      existingObjectTypes, isoMap, bomParameters: bomColumnParameters,
    }), wizard?.mapping
  );

  const validationEntries = new Map<string, string>();
  validationMap.forEach((entry) => {
    const key = (entry.key || entry.Key || '').trim().toUpperCase();
    const value = (entry.value || entry.Value || '').trim();
    if (key) {
      validationEntries.set(key, value);
    }
  });

  const allObjectTypeAttributes = objectTypeMap.get('ALL')?.attributes || [];
  const getAllObjectTypeValue = (pcfAttributeName: string): string => {
    const normalizedName = pcfAttributeName.trim().toLowerCase();
    const matchedAttribute = allObjectTypeAttributes.find(
      (attribute) =>
        (attribute.pcfAttributeName || '').trim().toLowerCase() === normalizedName
    );

    return typeof matchedAttribute?.revitParam === 'string'
      ? matchedAttribute.revitParam
      : '';
  };

  const groupingParameterWire = advancedBase?.GroupingParameter;
  const hasGroupingParameter = groupingParameterWire && typeof groupingParameterWire === 'object';
  const groupingParameterName = hasGroupingParameter && typeof groupingParameterWire.ParameterName === 'string'
    ? groupingParameterWire.ParameterName
    : store.menuConfiguration.groupingParameterName;
  const groupingParameterSource: 'Element' | 'Type' = hasGroupingParameter
    ? (groupingParameterWire.Source === 'Type' ? 'Type' : 'Element')
    : store.menuConfiguration.groupingParameterSource;

  // The base advanced-settings section is canonical: the export resolves the
  // grouping from it (resolvePcfGroupingConfiguration), so the store must
  // present the same mode. The wizard snapshot only applies when the base
  // section carries no preference at all, and keeps its raw value so the menu
  // page can flag invalid entries instead of hiding them.
  const hasBaseSystemGrouping =
    typeof advancedBase?.SystemGroupingPreference === 'string' ||
    typeof advancedBase?.SystemGroupingPreference === 'number';
  const systemGroupingValue = hasBaseSystemGrouping
    ? normalizeSystemGroupingPreference(
        advancedBase.SystemGroupingPreference,
        normalizeSystemGroupingPreference(store.menuConfiguration.systemGroupingValue, 'BySystemName')
      )
    : wizard
      ? wizard.menu.systemGroupingValue
      : normalizeSystemGroupingPreference(store.menuConfiguration.systemGroupingValue, 'BySystemName');

  const nextMenuConfiguration = {
    ...store.menuConfiguration,
    groupingParameterName,
    groupingParameterSource,
    selectedOption:
      profileUiMetadata?.workflowOption ??
      wizard?.menu.selectedOption ??
      store.menuConfiguration.selectedOption,
    sKeyValue: wizard
      ? wizard.menu.sKeyValue
      : validationEntries.get('SKEY') ||
        getAllObjectTypeValue('SKEY') ||
        store.menuConfiguration.sKeyValue,
    objectTypeValue: wizard
      ? wizard.menu.objectTypeValue
      : validationEntries.get('OBJECT-TYPE') ||
        getAllObjectTypeValue('OBJECT-TYPE') ||
        store.menuConfiguration.objectTypeValue,
    pipingSpecValue: wizard
      ? wizard.menu.pipingSpecValue
      : getAllObjectTypeValue('PIPING-SPEC') ||
        store.menuConfiguration.pipingSpecValue,
    systemGroupingValue,
  };

  const legacyConnectorBindingConfiguration = {
    ...store.connectorBindingConfiguration,
    endpoint1Value: {
      ...store.connectorBindingConfiguration.endpoint1Value,
      value:
        validationEntries.get('END-POINT_1') ||
        getAllObjectTypeValue('END-POINT_1') ||
        store.connectorBindingConfiguration.endpoint1Value.value,
    },
    endpoint2Value: {
      ...store.connectorBindingConfiguration.endpoint2Value,
      value:
        validationEntries.get('END-POINT_2') ||
        getAllObjectTypeValue('END-POINT_2') ||
        store.connectorBindingConfiguration.endpoint2Value.value,
    },
    endpoint3Value: {
      ...store.connectorBindingConfiguration.endpoint3Value,
      value:
        validationEntries.get('END-POINT_3') ||
        getAllObjectTypeValue('END-POINT_3') ||
        store.connectorBindingConfiguration.endpoint3Value.value,
    },
    endpoint4Value: {
      ...store.connectorBindingConfiguration.endpoint4Value,
      value:
        validationEntries.get('END-POINT_4') ||
        getAllObjectTypeValue('END-POINT_4') ||
        store.connectorBindingConfiguration.endpoint4Value.value,
    },
    materialValue: {
      ...store.connectorBindingConfiguration.materialValue,
      value:
        getAllObjectTypeValue('Material') ||
        store.connectorBindingConfiguration.materialValue.value,
    },
    designStdValue: {
      ...store.connectorBindingConfiguration.designStdValue,
      value:
        getAllObjectTypeValue('DesignStd') ||
        store.connectorBindingConfiguration.designStdValue.value,
    },
    pressureClassValue: {
      ...store.connectorBindingConfiguration.pressureClassValue,
      value:
        getAllObjectTypeValue('PressureClass') ||
        store.connectorBindingConfiguration.pressureClassValue.value,
    },
    insulationSpecValue: {
      ...store.connectorBindingConfiguration.insulationSpecValue,
      value:
        getAllObjectTypeValue('INSULATION-SPEC') ||
        store.connectorBindingConfiguration.insulationSpecValue.value,
    },
    insulationThicknessValue: {
      ...store.connectorBindingConfiguration.insulationThicknessValue,
      value:
        getAllObjectTypeValue('INSULATIONTHICKNESS') ||
        store.connectorBindingConfiguration.insulationThicknessValue.value,
    },
    weightValue: {
      ...store.connectorBindingConfiguration.weightValue,
      value:
        getAllObjectTypeValue('WEIGHT') ||
        store.connectorBindingConfiguration.weightValue.value,
    },
    tracingSpecValue: {
      ...store.connectorBindingConfiguration.tracingSpecValue,
      value:
        getAllObjectTypeValue('TRACING-SPEC') ||
        store.connectorBindingConfiguration.tracingSpecValue.value,
    },
    itemDescriptionValue: {
      ...store.connectorBindingConfiguration.itemDescriptionValue,
      value:
        getAllObjectTypeValue('ITEM-DESCRIPTION') ||
        store.connectorBindingConfiguration.itemDescriptionValue.value,
    },
  };

  const nextConnectorBindingConfiguration = wizard
    ? applyProfileFormFieldSnapshot(
        {
          ...legacyConnectorBindingConfiguration,
          parameterDestination: wizard.connector.parameterDestination,
          parameterBindingKind: wizard.connector.parameterBindingKind,
        },
        wizard.connector.fields,
        PIPEEX_UI_CONNECTOR_FIELD_IDS,
        isFullWizardProfile
      )
    : legacyConnectorBindingConfiguration;

  const parsedIsBoltParameter = getTextValue(advancedBase?.IsBoltParameter) || getTextValue(advancedBase?.isBoltParameter);
  const parsedBoltBaseFamily = normalizeBoltCalculationType(
    advancedBase?.BoltCalculationType,
    normalizeBoltCalculationType(store.boltConfiguration.boltBaseFamilyValue.value, 'GasketBased')
  );
  const parsedCenterOfCoordinates =
    getTextValue(advancedBase?.CenterOfCoordinates) === 'SurveyPoint' ||
    getTextValue(advancedBase?.centerOfCoordinates) === 'SurveyPoint'
      ? 'SurveyPoint'
      : 'ProjectBasePoint';
  const nextCenterOfCoordinates =
    wizard?.mapping.centerOfCoordinates ?? parsedCenterOfCoordinates;

  const legacyBoltConfiguration = {
    ...store.boltConfiguration,
    boltSetActiveValue: {
      ...store.boltConfiguration.boltSetActiveValue,
      value: parsedIsBoltParameter || store.boltConfiguration.boltSetActiveValue.value,
    },
    boltBaseFamilyValue: {
      ...store.boltConfiguration.boltBaseFamilyValue,
      value: parsedBoltBaseFamily,
    },
    boltModelingChoice: {
      ...store.boltConfiguration.boltModelingChoice,
      value: parsedBoltBaseFamily === 'BoltBased' ? 'yes' : 'no',
    },
  };

  const restoredBoltConfiguration = wizard
    ? applyProfileFormFieldSnapshot(
        {
          ...legacyBoltConfiguration,
          parameterDestination: wizard.bolt.parameterDestination,
          parameterBindingKind: wizard.bolt.parameterBindingKind,
          projectParameterCategories: [...wizard.bolt.projectParameterCategories],
        },
        wizard.bolt.fields,
        PIPEEX_UI_BOLT_FIELD_IDS,
        isFullWizardProfile
      )
    : legacyBoltConfiguration;
  const derivedBoltConfiguration = deriveBoltConfigurationFromProfileMappings(
    restoredBoltConfiguration,
    Array.from(objectTypeMap.values())
  );
  const hasLegacyBoltCalculationType =
    Object.prototype.hasOwnProperty.call(advancedBase ?? {}, 'BoltCalculationType') ||
    Object.prototype.hasOwnProperty.call(advancedBase ?? {}, 'boltCalculationType');
  const nextBoltConfiguration = !wizard && hasLegacyBoltCalculationType
    ? {
        ...derivedBoltConfiguration,
        boltBaseFamilyValue: restoredBoltConfiguration.boltBaseFamilyValue,
        boltModelingChoice: restoredBoltConfiguration.boltModelingChoice,
      }
    : derivedBoltConfiguration;
  debugPcfSettings('[pcfSettingsApply] profile bolt load resolution', {
    hadWizardBoltMetadata: Boolean(wizard?.bolt),
    metadataBolt: summarizeProfileBoltConfigurationForDebug(restoredBoltConfiguration),
    resolvedBolt: summarizeProfileBoltConfigurationForDebug(nextBoltConfiguration),
    mappingBoltRows: summarizeProfileBoltRowsForDebug(objectTypeMap),
  });
  stripBoltProfileRowsByChoice(objectTypeMap, nextBoltConfiguration);

  const nextAdditionalCategoriesConfiguration = wizard
    ? applyProfileFormFieldSnapshot(
        {
          ...store.additionalCategoriesConfiguration,
          parameterDestination: wizard.additionalCategories.parameterDestination,
          parameterBindingKind: wizard.additionalCategories.parameterBindingKind,
          projectParameterCategories: [
            ...wizard.additionalCategories.projectParameterCategories,
          ],
        },
        wizard.additionalCategories.fields,
        PIPEEX_UI_ADDITIONAL_CATEGORIES_FIELD_IDS,
        isFullWizardProfile
      )
    : store.additionalCategoriesConfiguration;

  const nextQuickFillConfiguration = restoreQuickFillProfileConfiguration(
    profileUiMetadata?.quickFill,
    nextMenuConfiguration,
    nextConnectorBindingConfiguration
  );

  const nextProcessPropertiesConfiguration = {
    ...store.processPropertiesConfiguration,
    processConditionsChecked:
      wizard?.processProperties.processConditionsChecked ??
      profileUiMetadata?.workflow.processConditionsChecked ??
      store.processPropertiesConfiguration.processConditionsChecked,
    projectPropertiesChecked:
      wizard?.processProperties.projectPropertiesChecked ??
      profileUiMetadata?.workflow.projectPropertiesChecked ??
      store.processPropertiesConfiguration.projectPropertiesChecked,
    includeDefaultPipeexParameters: wizard?.processProperties.includeDefaultPipeexParameters ?? store.processPropertiesConfiguration.includeDefaultPipeexParameters,
    processConditionsValue: {
      ...store.processPropertiesConfiguration.processConditionsValue,
      value: getProfileProcessValue(
        'processConditionsValue',
        getNamedParameterValue(systemParameters, 'PROCESS-CONDITIONS') ||
          store.processPropertiesConfiguration.processConditionsValue.value
      ),
    },
    projectPropertiesValue: {
      ...store.processPropertiesConfiguration.projectPropertiesValue,
      value: getProfileProcessValue(
        'projectPropertiesValue',
        getNamedParameterValue(projectParameters, 'PROJECT-PROPERTIES') ||
          store.processPropertiesConfiguration.projectPropertiesValue.value
      ),
    },
    pipingSpecValuePC: {
      ...store.processPropertiesConfiguration.pipingSpecValuePC,
      value: getProfileProcessValue(
        'pipingSpecValuePC',
        getNamedParameterValue(systemParameters, 'PIPING-SPEC') ||
          store.processPropertiesConfiguration.pipingSpecValuePC.value
      ),
    },
    tracingSpecValuePC: {
      ...store.processPropertiesConfiguration.tracingSpecValuePC,
      value: getProfileProcessValue(
        'tracingSpecValuePC',
        getNamedParameterValue(systemParameters, 'TRACING-SPEC') ||
          store.processPropertiesConfiguration.tracingSpecValuePC.value
      ),
    },
    attribute12Value: {
      ...store.processPropertiesConfiguration.attribute12Value,
      value: getProfileProcessValue(
        'attribute12Value',
        getNamedParameterValue(systemParameters, 'ATTRIBUTE12') ||
          store.processPropertiesConfiguration.attribute12Value.value
      ),
    },
    attribute13Value: {
      ...store.processPropertiesConfiguration.attribute13Value,
      value: getProfileProcessValue(
        'attribute13Value',
        getNamedParameterValue(systemParameters, 'ATTRIBUTE13') ||
          store.processPropertiesConfiguration.attribute13Value.value
      ),
    },
    attribute14Value: {
      ...store.processPropertiesConfiguration.attribute14Value,
      value: getProfileProcessValue(
        'attribute14Value',
        getNamedParameterValue(systemParameters, 'ATTRIBUTE14') ||
          store.processPropertiesConfiguration.attribute14Value.value
      ),
    },
    attribute15Value: {
      ...store.processPropertiesConfiguration.attribute15Value,
      value: getProfileProcessValue(
        'attribute15Value',
        getNamedParameterValue(systemParameters, 'ATTRIBUTE15') ||
          store.processPropertiesConfiguration.attribute15Value.value
      ),
    },
    attribute16Value: {
      ...store.processPropertiesConfiguration.attribute16Value,
      value: getProfileProcessValue(
        'attribute16Value',
        getNamedParameterValue(systemParameters, 'ATTRIBUTE16') ||
          store.processPropertiesConfiguration.attribute16Value.value
      ),
    },
    attribute17Value: {
      ...store.processPropertiesConfiguration.attribute17Value,
      value: getProfileProcessValue(
        'attribute17Value',
        getNamedParameterValue(systemParameters, 'ATTRIBUTE17') ||
          store.processPropertiesConfiguration.attribute17Value.value
      ),
    },
    attribute18Value: {
      ...store.processPropertiesConfiguration.attribute18Value,
      value: getProfileProcessValue(
        'attribute18Value',
        getNamedParameterValue(systemParameters, 'ATTRIBUTE18') ||
          store.processPropertiesConfiguration.attribute18Value.value
      ),
    },
    attribute19Value: {
      ...store.processPropertiesConfiguration.attribute19Value,
      value: getProfileProcessValue(
        'attribute19Value',
        getNamedParameterValue(systemParameters, 'ATTRIBUTE19') ||
          store.processPropertiesConfiguration.attribute19Value.value
      ),
    },
    attribute20Value: {
      ...store.processPropertiesConfiguration.attribute20Value,
      value: getProfileProcessValue(
        'attribute20Value',
        getNamedParameterValue(systemParameters, 'ATTRIBUTE20') ||
          store.processPropertiesConfiguration.attribute20Value.value
      ),
    },
    attribute21Value: {
      ...store.processPropertiesConfiguration.attribute21Value,
      value: getProfileProcessValue(
        'attribute21Value',
        getNamedParameterValue(systemParameters, 'ATTRIBUTE21') ||
          store.processPropertiesConfiguration.attribute21Value.value
      ),
    },
    attribute22Value: {
      ...store.processPropertiesConfiguration.attribute22Value,
      value: getProfileProcessValue(
        'attribute22Value',
        getNamedParameterValue(systemParameters, 'ATTRIBUTE22') ||
          store.processPropertiesConfiguration.attribute22Value.value
      ),
    },
    attribute23Value: {
      ...store.processPropertiesConfiguration.attribute23Value,
      value: getProfileProcessValue(
        'attribute23Value',
        getNamedParameterValue(systemParameters, 'ATTRIBUTE23') ||
          store.processPropertiesConfiguration.attribute23Value.value
      ),
    },
    projectIdentifierValue: {
      ...store.processPropertiesConfiguration.projectIdentifierValue,
      value: getProfileProcessValue(
        'projectIdentifierValue',
        getNamedParameterValue(projectParameters, 'PROJECT-IDENTIFIER') ||
          store.processPropertiesConfiguration.projectIdentifierValue.value
      ),
    },
    projectNameValue: {
      ...store.processPropertiesConfiguration.projectNameValue,
      value: getProfileProcessValue(
        'projectNameValue',
        getNamedParameterValue(projectParameters, 'PROJECT-NAME') ||
          store.processPropertiesConfiguration.projectNameValue.value
      ),
    },
    areaValue: {
      ...store.processPropertiesConfiguration.areaValue,
      value: getProfileProcessValue(
        'areaValue',
        getNamedParameterValue(projectParameters, 'AREA') ||
          store.processPropertiesConfiguration.areaValue.value
      ),
    },
    attribute1ClientName: {
      ...store.processPropertiesConfiguration.attribute1ClientName,
      value: getProfileProcessValue(
        'attribute1ClientName',
        getNamedParameterValue(projectParameters, 'ATTRIBUTE1') ||
          store.processPropertiesConfiguration.attribute1ClientName.value
      ),
    },
    attribute2ProjectAddress: {
      ...store.processPropertiesConfiguration.attribute2ProjectAddress,
      value: getProfileProcessValue(
        'attribute2ProjectAddress',
        getNamedParameterValue(projectParameters, 'ATTRIBUTE2') ||
          store.processPropertiesConfiguration.attribute2ProjectAddress.value
      ),
    },
    attribute3ClientDepartment: {
      ...store.processPropertiesConfiguration.attribute3ClientDepartment,
      value: getProfileProcessValue(
        'attribute3ClientDepartment',
        getNamedParameterValue(projectParameters, 'ATTRIBUTE3') ||
          store.processPropertiesConfiguration.attribute3ClientDepartment.value
      ),
    },
    attribute4ClientProjectNumber: {
      ...store.processPropertiesConfiguration.attribute4ClientProjectNumber,
      value: getProfileProcessValue(
        'attribute4ClientProjectNumber',
        getNamedParameterValue(projectParameters, 'ATTRIBUTE4') ||
          store.processPropertiesConfiguration.attribute4ClientProjectNumber.value
      ),
    },
    attribute5ClientDocumentNumber: {
      ...store.processPropertiesConfiguration.attribute5ClientDocumentNumber,
      value: getProfileProcessValue(
        'attribute5ClientDocumentNumber',
        getNamedParameterValue(projectParameters, 'ATTRIBUTE5') ||
          store.processPropertiesConfiguration.attribute5ClientDocumentNumber.value
      ),
    },
    attribute6ClientCode: {
      ...store.processPropertiesConfiguration.attribute6ClientCode,
      value: getProfileProcessValue(
        'attribute6ClientCode',
        getNamedParameterValue(projectParameters, 'ATTRIBUTE6') ||
          store.processPropertiesConfiguration.attribute6ClientCode.value
      ),
    },
    attribute7EngPartyOffice: {
      ...store.processPropertiesConfiguration.attribute7EngPartyOffice,
      value: getProfileProcessValue(
        'attribute7EngPartyOffice',
        getNamedParameterValue(projectParameters, 'ATTRIBUTE7') ||
          store.processPropertiesConfiguration.attribute7EngPartyOffice.value
      ),
    },
    attribute8EngPartyOfficeLocation: {
      ...store.processPropertiesConfiguration.attribute8EngPartyOfficeLocation,
      value: getProfileProcessValue(
        'attribute8EngPartyOfficeLocation',
        getNamedParameterValue(projectParameters, 'ATTRIBUTE8') ||
          store.processPropertiesConfiguration.attribute8EngPartyOfficeLocation.value
      ),
    },
    attribute9EngPartyProjectNumber: {
      ...store.processPropertiesConfiguration.attribute9EngPartyProjectNumber,
      value: getProfileProcessValue(
        'attribute9EngPartyProjectNumber',
        getNamedParameterValue(projectParameters, 'ATTRIBUTE9') ||
          store.processPropertiesConfiguration.attribute9EngPartyProjectNumber.value
      ),
    },
    attribute10EngPartyAreaCode: {
      ...store.processPropertiesConfiguration.attribute10EngPartyAreaCode,
      value: getProfileProcessValue(
        'attribute10EngPartyAreaCode',
        getNamedParameterValue(projectParameters, 'ATTRIBUTE10') ||
          store.processPropertiesConfiguration.attribute10EngPartyAreaCode.value
      ),
    },
  };

  store.loadConfiguration({
    menuConfiguration: nextMenuConfiguration,
    connectorBindingConfiguration: nextConnectorBindingConfiguration,
    processPropertiesConfiguration: nextProcessPropertiesConfiguration,
    boltConfiguration: nextBoltConfiguration,
    additionalCategoriesConfiguration: nextAdditionalCategoriesConfiguration,
    ...(wizard?.progress
      ? {
          hasSavedOnce: wizard.progress.hasSavedOnce,
          isWorkflowApplied: wizard.progress.isWorkflowApplied,
        }
      : {}),
    mappingConfiguration: {
      ...store.mappingConfiguration,
      advanced: {
        ...store.mappingConfiguration.advanced,
        centerOfCoordinates: nextCenterOfCoordinates,
      },
      general: {
        ...store.mappingConfiguration.general,
        mappingTable: {
          ...store.mappingConfiguration.general.mappingTable,
          objectType: Array.from(objectTypeMap.values()),
        },
        projectProperties: isFullWizardProfile
          ? mappedProjectProperties
          : mappedProjectProperties.length > 0
            ? mappedProjectProperties
            : store.mappingConfiguration.general.projectProperties,
        pipingSystemProperties: isFullWizardProfile
          ? mappedSystemProperties
          : mappedSystemProperties.length > 0
            ? mappedSystemProperties
            : store.mappingConfiguration.general.pipingSystemProperties,
      },
    },
  });

  applyMappingScopeEditorProfileState(profileUiMetadata?.scopeEditors);
  restoreParameterWorkflowProfileSnapshot(wizard);

  const restoredWorkflowOption =
    profileUiMetadata?.workflowOption ?? wizard?.menu.selectedOption ?? null;
  if (restoredWorkflowOption) {
    uiStore.setSelectedOption(restoredWorkflowOption);
  }

  if (options.restoreWizardProgress !== false) {
    uiStore.restoreWizardProfileProgress(wizard?.progress);
  } else if (wizard?.progress) {
    debugPcfSettings('[pcfSettingsClient] apply skipped wizard progress restore', {
      savedScreen: wizard.progress.screen,
      currentScreen: useUIStore.getState().screen,
    });
  }
  quickFillStore.replaceProfileConfiguration(nextQuickFillConfiguration);
  const afterSnapshot = snapshotCurrentPcfSettingsStoreForDebug();
  debugPcfSettings('[pcfSettingsApply] applyPipeexSettingsToConfigurationStore complete', {
    afterProcess: afterSnapshot.process,
    afterStore: afterSnapshot,
    workflowOption: afterSnapshot.selectedWorkflowOption,
  });
}
